import type {
  AppState,
  BinaryFiles,
} from "@excalidraw/excalidraw/types";
import type { OrderedExcalidrawElement } from "@excalidraw/element/types";

export interface DrawingSummary {
  id: string;
  name: string;
  thumbnail: string | null;
  created_at: string;
  updated_at: string;
}

export interface DrawingData {
  drawing: {
    id: string;
    name: string;
    elements: readonly OrderedExcalidrawElement[];
    app_state: Partial<AppState>;
    thumbnail: string | null;
    created_at: string;
    updated_at: string;
  };
  files: Array<{
    id: string;
    mimeType: string;
    dataURL: string;
  }>;
}

const apiFetch = async (url: string, options?: RequestInit) => {
  const res = await fetch(url, {
    credentials: "include",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Request failed: ${res.status}`);
  }

  return res.json();
};

export const ServerData = {
  async list(): Promise<DrawingSummary[]> {
    const data = await apiFetch("/api/drawings");
    return data.drawings;
  },

  async load(drawingId: string): Promise<DrawingData> {
    return apiFetch(`/api/drawings/${drawingId}`);
  },

  async create(name?: string): Promise<{ id: string; name: string }> {
    return apiFetch("/api/drawings", {
      method: "POST",
      body: JSON.stringify({ name: name || "Untitled" }),
    });
  },

  async save(
    drawingId: string,
    elements: readonly OrderedExcalidrawElement[],
    appState: Partial<AppState>,
    thumbnail?: string,
  ): Promise<void> {
    await apiFetch(`/api/drawings/${drawingId}`, {
      method: "PUT",
      body: JSON.stringify({
        elements,
        appState: {
          viewBackgroundColor: appState.viewBackgroundColor,
          currentItemFontFamily: appState.currentItemFontFamily,
          theme: appState.theme,
          name: appState.name,
        },
        thumbnail,
      }),
    });
  },

  async rename(drawingId: string, name: string): Promise<void> {
    await apiFetch(`/api/drawings/${drawingId}`, {
      method: "PUT",
      body: JSON.stringify({ name }),
    });
  },

  async delete(drawingId: string): Promise<void> {
    await apiFetch(`/api/drawings/${drawingId}`, {
      method: "DELETE",
    });
  },

  async uploadFiles(
    drawingId: string,
    files: BinaryFiles,
    knownFileIds: Set<string>,
  ): Promise<void> {
    const newFiles = Object.entries(files).filter(
      ([id]) => !knownFileIds.has(id),
    );

    if (newFiles.length === 0) {
      return;
    }

    const formData = new FormData();
    for (const [id, file] of newFiles) {
      // Convert dataURL to blob
      const response = await fetch(file.dataURL);
      const blob = await response.blob();
      formData.append("files", blob, id);
    }

    const res = await fetch(`/api/drawings/${drawingId}/files`, {
      method: "POST",
      credentials: "include",
      body: formData,
    });

    if (!res.ok) {
      throw new Error(`File upload failed: ${res.status}`);
    }

    // Track uploaded files
    for (const [id] of newFiles) {
      knownFileIds.add(id);
    }
  },
};
